import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { authAPI, authUtils } from './auth'
import { userAPI, subscriptionAPI, lcaAPI, apiClient } from './client'
import { toast } from 'sonner'
import { toolSelectionAPI } from './toolSelection'
import type { ApiRequestError } from '@/lib/types'

// Auth hooks updated for UUID refactor - no more username
export const useLogin = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      authAPI.login(email, password),
    onSuccess: (data) => {
      authUtils.setToken(data.data.access_token)
      if (data.data.refresh_token) {
        authUtils.setRefreshToken(data.data.refresh_token)
      }
      // Enable the profile query by invalidating it
      queryClient.invalidateQueries({ queryKey: ['user', 'profile'] })
    },
  })
}

export const useSignup = () => {
  return useMutation({
    mutationFn: ({ email, password, name }: { email: string; password: string; name?: string }) =>
      authAPI.signup(email, password, name),
  })
}

export const useLogout = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      // Cancel all queries first to prevent races
      await queryClient.cancelQueries()
      // Server logout while token is still in storage
      try {
        await authAPI.logout()
      } catch {
        /* ignore */
      }
      // Now clear everything
      authUtils.removeToken()
      authUtils.removeRefreshToken()
      queryClient.clear()
    },
  })
}

export const useProfile = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['user', 'profile'],
    queryFn: () => authAPI.getProfile().then((res) => res.data),
    enabled: options?.enabled ?? !!authUtils.getToken(), // Use passed enabled or default logic
    retry: false, // Don't retry failed requests
    refetchOnWindowFocus: false, // Don't refetch on window focus
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  })
}

export const useVerifyEmail = () => {
  return useMutation({
    mutationFn: (token: string) => authAPI.verifyEmail(token),
  })
}

export const useSendVerificationEmail = () => {
  return useMutation({
    mutationFn: (email: string) => authAPI.sendVerificationEmail(email),
  })
}

export const usePasswordReset = () => {
  return useMutation({
    mutationFn: (email: string) => authAPI.sendPasswordReset(email),
  })
}

export const useResetPassword = () => {
  return useMutation({
    mutationFn: ({ token, new_password }: { token: string; new_password: string }) =>
      authAPI.resetPassword(token, new_password),
  })
}

// Dashboard hooks for user data
export const useUserTier = () => {
  return useQuery({
    queryKey: ['user', 'tier'],
    queryFn: () => userAPI.getUserWithTier().then((res) => res.data),
    enabled: !!authUtils.getToken(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  })
}

export const useUsageData = () => {
  return useQuery({
    queryKey: ['user', 'usage'],
    queryFn: () => userAPI.getUsage().then((res) => res.data),
    enabled: !!authUtils.getToken(),
    staleTime: 60 * 1000, // 1 minute (usage changes frequently)
    refetchOnWindowFocus: false,
  })
}

export const useDetailedUsage = () => {
  return useQuery({
    queryKey: ['user', 'detailed-usage'],
    queryFn: () => userAPI.getDetailedUsage().then((res) => res.data),
    enabled: !!authUtils.getToken(),
    staleTime: 60 * 1000, // 1 minute
    refetchOnWindowFocus: false,
  })
}

export const useDashboardBalances = () => {
  return useQuery({
    queryKey: ['user', 'dashboard-balances'],
    queryFn: () => userAPI.getDashboardBalances().then((res) => res.data),
    enabled: !!authUtils.getToken(),
    staleTime: 60 * 1000, // 1 minute
    refetchOnWindowFocus: false,
  })
}

export const useCreditUsageBreakdown = () => {
  return useQuery({
    queryKey: ['user', 'credit-usage'],
    queryFn: () => apiClient.get('/dashboard/credit-usage').then((res) => res.data),
    enabled: !!authUtils.getToken(),
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

export const useCreditLedger = () => {
  return useQuery({
    queryKey: ['user', 'credit-ledger'],
    queryFn: () => apiClient.get('/dashboard/credit-ledger').then((res) => res.data),
    enabled: !!authUtils.getToken(),
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

export const useCreditUsageHistory = () => {
  return useQuery({
    queryKey: ['user', 'credit-usage-history'],
    queryFn: () => apiClient.get('/dashboard/credit-usage-history').then((res) => res.data),
    enabled: !!authUtils.getToken(),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

export const useApiActivity = (options?: { limit?: number; hours?: number }) => {
  return useQuery({
    queryKey: ['user', 'api-activity', options],
    queryFn: () => userAPI.getApiActivity(options).then((res) => res.data),
    enabled: !!authUtils.getToken(),
    staleTime: 30 * 1000, // 30 seconds (activity is very current)
    refetchOnWindowFocus: false,
  })
}

export const useCallPackHistory = () => {
  return useQuery({
    queryKey: ['user', 'call-pack-history'],
    queryFn: () => userAPI.getCallPackHistory().then((res) => res.data),
    enabled: !!authUtils.getToken(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  })
}

export const useCallPackPricing = () => {
  return useQuery({
    queryKey: ['call-pack-pricing'],
    queryFn: () => userAPI.getCallPackPricing().then((res) => res.data),
    staleTime: 15 * 60 * 1000, // 15 minutes (pricing doesn't change often)
    refetchOnWindowFocus: false,
  })
}

export const useApiTokens = () => {
  return useQuery({
    queryKey: ['user', 'api-tokens'],
    queryFn: () => userAPI.getApiTokens().then((res) => res.data),
    enabled: !!authUtils.getToken(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  })
}

export const useOAuthTokens = () => {
  return useQuery({
    queryKey: ['user', 'oauth-tokens'],
    queryFn: () => userAPI.getOAuthTokens().then((res) => res.data),
    enabled: !!authUtils.getToken(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  })
}

export const useMcpConfig = () => {
  return useQuery({
    queryKey: ['user', 'mcp-config'],
    queryFn: () => userAPI.getMcpConfig().then((res) => res.data),
    enabled: !!authUtils.getToken(),
    staleTime: 10 * 60 * 1000, // 10 minutes (config doesn't change often)
    refetchOnWindowFocus: false,
  })
}

export const useSubscriptionStatus = () => {
  return useQuery({
    queryKey: ['user', 'subscription'],
    queryFn: () => subscriptionAPI.getStatus().then((res) => res.data),
    enabled: !!authUtils.getToken(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  })
}

export const useSubscriptionProducts = () => {
  return useQuery({
    queryKey: ['subscription-products'],
    queryFn: () => subscriptionAPI.getSubscriptionProducts().then((res) => res.data),
    staleTime: 15 * 60 * 1000, // 15 minutes (products don't change often)
    refetchOnWindowFocus: false,
  })
}

export const useTiersWithTools = () => {
  return useQuery({
    queryKey: ['tiers-with-tools'],
    queryFn: () => subscriptionAPI.getTiersWithTools().then((res) => res.data),
    enabled: !!authUtils.getToken(),
    staleTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

export const useCalculationHistory = () => {
  return useQuery({
    queryKey: ['user', 'calculation-history'],
    queryFn: () => lcaAPI.getCalculationHistory().then((res) => res.data),
    enabled: !!authUtils.getToken(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: false,
  })
}

// Chat Usage Tracking
export const useChatUsage = () => {
  return useQuery({
    queryKey: ['chat', 'usage'],
    queryFn: () => userAPI.getChatUsage().then((res) => res.data),
    enabled: !!authUtils.getToken(),
    staleTime: 30 * 1000, // 30 seconds (usage changes frequently)
    refetchInterval: 30 * 1000, // Auto-refresh every 30 seconds
    refetchOnWindowFocus: true,
  })
}

// Chat Models (for playground model selector)
export const useChatModels = () => {
  return useQuery({
    queryKey: ['chat', 'models'],
    queryFn: async () => {
      const { chatService } = await import('@/lib/api/chat')
      return chatService.getModels()
    },
    enabled: !!authUtils.getToken(),
    staleTime: 15 * 60 * 1000, // 15 minutes (models don't change often)
    refetchOnWindowFocus: false,
  })
}

// Conversation credits (actual backend data)
export interface ConversationCreditsData {
  conversation_uuid: string
  total_credits: number
  breakdown: Record<string, { credits: number; count: number }>
  total_cost?: number // present only when the user is allowed to see provider cost
}

export const useConversationCredits = (conversationUuid: string | null) => {
  return useQuery({
    queryKey: ['chat', 'conversation-credits', conversationUuid],
    queryFn: () =>
      apiClient
        .get<ConversationCreditsData>(`/chat/conversations/${conversationUuid}/credits`)
        .then((res) => res.data),
    enabled: !!authUtils.getToken() && !!conversationUuid,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  })
}

// Call Pack Checkout - Navigate to checkout page like subscriptions
export const useCallPackCheckout = () => {
  const navigate = useNavigate()

  return useMutation({
    mutationFn: ({
      paddlePriceId,
      packSize,
      productId,
      quantity,
    }: {
      paddlePriceId: string
      packSize: number
      productId: number
      quantity?: number
    }) => {
      // Navigate to checkout page with paddle price ID and product ID
      navigate(
        `/checkout?type=callpack&paddle_id=${paddlePriceId}&pack_size=${packSize}&product_id=${productId}&quantity=${quantity || 1}`,
      )
      return Promise.resolve() // Return resolved promise for mutation
    },
    onError: (error: ApiRequestError) => {
      toast.error('Failed to initiate checkout' + (error?.message ? `: ${error.message}` : ''))
    },
  })
}

// Account Deletion
export const useDeleteAccount = () => {
  return useMutation({
    mutationFn: () => userAPI.deleteAccount(),
    onSuccess: () => {
      // Don't clear data or redirect immediately - let the component handle it
      // This allows showing a proper goodbye message
    },
    onError: (error: ApiRequestError) => {
      toast.error(
        'Failed to delete account' +
          (error?.response?.data?.detail ? `: ${error.response.data.detail}` : ''),
      )
    },
  })
}

// Email Preferences
export const useEmailPreferences = () => {
  return useQuery({
    queryKey: ['email-preferences'],
    queryFn: () => apiClient.get('/email-preferences/me').then((res) => res.data),
    enabled: !!authUtils.getToken(),
    staleTime: 5 * 60 * 1000,
  })
}

export const useUpdateEmailPreferences = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (prefs: {
      product_updates?: boolean
      feedback_requests?: boolean
      promotions?: boolean
      newsletter?: boolean
    }) => apiClient.patch('/email-preferences/me', prefs).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-preferences'] })
      toast.success('Email preferences updated')
    },
    onError: (error: ApiRequestError) => {
      toast.error(
        'Failed to update preferences' +
          (error?.response?.data?.detail ? `: ${error.response.data.detail}` : ''),
      )
    },
  })
}

// Feature Flags
export const useFeatureFlags = () => {
  return useQuery({
    queryKey: ['feature-flags'],
    queryFn: () => userAPI.getFeatures().then((res) => res.data.features),
    enabled: !!authUtils.getToken(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  })
}

export const useFeatureFlag = (featureName: string) => {
  const { data: features } = useFeatureFlags()
  return features?.[featureName] ?? false
}

// Playground MCP Servers
export interface PlaygroundMcpServer {
  uuid: string
  name: string
  description: string | null
  is_default: boolean
  required_feature: string | null
  has_feature: boolean
  is_enabled: boolean
  can_toggle: boolean
}

export const usePlaygroundMcpServers = () => {
  return useQuery({
    queryKey: ['playground-mcp-servers'],
    queryFn: () =>
      userAPI.getPlaygroundMcpServers().then((res) => res.data.servers as PlaygroundMcpServer[]),
    enabled: !!authUtils.getToken(),
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: true,
  })
}

export const useTogglePlaygroundMcpServer = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ serverUuid, enabled }: { serverUuid: string; enabled: boolean }) =>
      userAPI
        .togglePlaygroundMcpServer(serverUuid, enabled)
        .then((res) => res.data as PlaygroundMcpServer),
    onMutate: async ({ serverUuid, enabled }) => {
      await queryClient.cancelQueries({ queryKey: ['playground-mcp-servers'] })
      const previous = queryClient.getQueryData<PlaygroundMcpServer[]>(['playground-mcp-servers'])
      queryClient.setQueryData<PlaygroundMcpServer[]>(['playground-mcp-servers'], (old) =>
        old?.map((s) => (s.uuid === serverUuid ? { ...s, is_enabled: enabled } : s)),
      )
      return { previous }
    },
    onSuccess: (updatedServer) => {
      // Update cache with server response instead of refetching
      queryClient.setQueryData<PlaygroundMcpServer[]>(['playground-mcp-servers'], (old) =>
        old?.map((s) => (s.uuid === updatedServer.uuid ? { ...s, ...updatedServer } : s)),
      )
    },
    onError: (error: ApiRequestError, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['playground-mcp-servers'], context.previous)
      }
      toast.error(
        'Failed to toggle MCP server' +
          (error?.response?.data?.detail ? `: ${error.response.data.detail}` : ''),
      )
      // Only refetch on error to ensure cache is consistent
      queryClient.invalidateQueries({ queryKey: ['playground-mcp-servers'] })
    },
  })
}

// Custom System Prompt
export const useCustomSystemPrompt = () => {
  return useQuery({
    queryKey: ['custom-system-prompt'],
    queryFn: () =>
      userAPI.getCustomSystemPrompt().then(
        (res) =>
          res.data as {
            custom_system_prompt: string | null
            default_system_prompt: string
            max_length: number
          },
      ),
    enabled: !!authUtils.getToken(),
  })
}

export const useSetCustomSystemPrompt = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (customSystemPrompt: string | null) =>
      userAPI.setCustomSystemPrompt(customSystemPrompt).then((res) => res.data),
    onSuccess: (data) => {
      queryClient.setQueryData(['custom-system-prompt'], data)
      toast.success('System prompt updated')
    },
    onError: (error: ApiRequestError) => {
      toast.error(
        'Failed to update system prompt' +
          (error?.response?.data?.detail ? `: ${error.response.data.detail}` : ''),
      )
    },
  })
}

// ---------------------------------------------------------------------------
// Tool Selection hooks
// ---------------------------------------------------------------------------

export const useToolCatalog = () =>
  useQuery({
    queryKey: ['chat', 'tool-catalog'],
    queryFn: () => toolSelectionAPI.getCatalog(),
    enabled: !!authUtils.getToken(),
    staleTime: 60 * 1000,
  })

export const useToolDefaults = () =>
  useQuery({
    queryKey: ['chat', 'tool-defaults'],
    queryFn: () => toolSelectionAPI.getDefaults(),
    enabled: !!authUtils.getToken(),
  })

export const useSetToolDefaults = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (enabledTools: string[]) => toolSelectionAPI.setDefaults(enabledTools),
    onSuccess: (data) => qc.setQueryData(['chat', 'tool-defaults'], data),
  })
}

export const useConversationTools = (uuid: string | null) =>
  useQuery({
    queryKey: ['chat', 'conversation-tools', uuid],
    queryFn: () => toolSelectionAPI.getConversationTools(uuid as string),
    enabled: !!authUtils.getToken() && !!uuid,
  })

export const useAddConversationTools = (uuid: string | null) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (enable: string[]) => toolSelectionAPI.addConversationTools(uuid as string, enable),
    onSuccess: (data) => qc.setQueryData(['chat', 'conversation-tools', uuid], data),
  })
}

